import React, { useState, useMemo, useEffect } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Home from "./pages/Home";
import ServiceDetailsPage from "./pages/ServiceDetailsPage";
import Header from "./components/Header";
import Footer from "./components/Footer";
import "./i18n/i18n";
import LoginScreen from "./pages/LoginScreen";
import SignUpScreen from "./pages/SignUpScreen";
import ForgotPasswordScreen from "./pages/ForgotPasswordScreen";
import VerifyCodeScreen from "./pages/VerifyCodeScreen";
import SetPasswordScreen from "./pages/SetPasswordScreen";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CartPage from "./pages/CartPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import UserForm from "./pages/UserForm";
import ProtectedRoute from "./components/ProtectedRoute";
import FamilyManagementPage from "./pages/FamilyManagementPage";
import AdminProfile from "./pages/AdminProfile";
import ProductForm from "./pages/ProductForm";
import FamilyHome from "./pages/FamilyHome";
import FamilyProfile from "./pages/FamilyProfile";
import UserProfile from "./pages/UserProfile";
import FamilyOrders from "./pages/FamilyOrders";
import ContactUs from "./pages/ContactUs";
import { orange, green } from "@mui/material/colors";
import LoadingComponent from "./components/LoadingComponent";
import AdminManagementPage from "./pages/AdminManagementPage";
import NotFound from "./pages/NotFound";
import WelcomePage from "./pages/WelcomePage";

export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

const RootRedirect = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate user fetching from localStorage or API
    const fetchUser = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error("Error fetching user", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (loading) {
    return <LoadingComponent />;
  }

  const endWelcome = localStorage.getItem("endWelcome")
  if(!user && !endWelcome)  {
    return <Navigate to="/welcomePage" replace />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.type) {
    case "admin":
      return <Navigate to="/adminHome" replace />;
    case "family":
      return <Navigate to="/familyHome" replace />;
    case "client":
      return <Navigate to="/home" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  const [mode, setMode] = useState("light");
  const { i18n } = useTranslation();
  const location = useLocation();

  // Update direction and language in localStorage
  useEffect(() => {
    const storedMode = localStorage.getItem("theme-mode");
    if (storedMode) {
      setMode(storedMode);
    }

    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
      document.documentElement.dir = storedLanguage === "ar" ? "rtl" : "ltr";
    } else {
      document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    }
  }, [i18n]);

  // Listen for language changes to update RTL/LTR
  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    localStorage.setItem("language", i18n.language);
  }, [i18n.language]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          localStorage.setItem("theme-mode", newMode);
          return newMode;
        });
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        direction: i18n.language === "ar" ? "rtl" : "ltr",
        palette: {
          mode,
          primary: {
            main: mode === "light" ? "#246D5C" : "#00B3A9",
            mainLoginBackground: mode === "light" ? "#E8F5F3" : "#0D2A23",
            mainTextFieldBackground: mode === "light" ? "#ffffff" : "#1E1E1E",
            mainOwnerBackground: mode === "light" ? "#F0F9F7" : "#132B25",
            mainOwnerColor: mode === "light" ? "#191C32" : "#E0F0EC",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#E4FF03",
          },
          warning: {
            main: mode === "light" ? orange[700] : orange[500],
            contrastText: "#fff",
          },
          success: {
            main: mode === "light" ? green[600] : green[400],
            contrastText: "#fff",
          },
          accent: {
            main: "#00B3A9",
            light: "#4FC9C1",
            dark: "#00857D",
          },
          gradients: {
            main:
              mode === "light"
                ? "linear-gradient(220deg, rgba(2, 2, 2, 0.22) 0%, #3EA38C 100%)"
                : "linear-gradient(220deg, #020202 0%, #0D2A23 100%)",
          },
          blackandWhite: {
            main: mode === "light" ? "#191C32" : "#ffffff",
          },
          background: {
            default: mode === "light" ? "#ffffff" : "#121212",
            paper: mode === "light" ? "#ffffff" : "#1e1e1e",
          },
        },
        typography: {
          fontFamily: ["Cairo"].join(","),
          // ... keep existing typography settings
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: `          
          body {
          mode === 'light'
  ? 'linear-gradient(220deg, rgba(0, 0, 0, 0.05) 0%, #A8D5CD 100%)'
  : 'linear-gradient(220deg, #1A1A1A 0%, #144D43 100%)'

            background-attachment: fixed;
            background-size: cover;
            min-height: 100vh;
            margin: 0;
            padding: 0;
            
            scrollbar-color: ${
              mode === "dark" ? "#246D5C #0D2A23" : "#3EA38C #E8F5F3"
            };
          }
          
          body::-webkit-scrollbar, *::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          body::-webkit-scrollbar-thumb, *::-webkit-scrollbar-thumb {
            border-radius: 8px;
            background-color: ${mode === "dark" ? "#246D5C" : "#3EA38C"};
          }
          
          body::-webkit-scrollbar-thumb:hover {
            background-color: ${mode === "dark" ? "#00B3A9" : "#246D5C"};
          }
          
          body::-webkit-scrollbar-track, *::-webkit-scrollbar-track {
            border-radius: 8px;
            background-color: ${mode === "dark" ? "#0D2A23" : "#E8F5F3"};
          }
        `,
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: "8px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                },
              },
              containedPrimary: {
                backgroundColor: "#246D5C",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#1A4A3D" : "#00B3A9",
                },
              },
              containedSecondary: {
                backgroundColor: "#E4FF03",
                color: "#000000",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#C9E000" : "#F0FF70",
                },
              },
              outlinedPrimary: {
                borderColor: "#246D5C",
                color: "#246D5C",
                "&:hover": {
                  backgroundColor: "rgba(36, 109, 92, 0.1)",
                  borderColor: mode === "light" ? "#1A4A3D" : "#00B3A9",
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor:
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.85)"
                    : "rgba(30, 30, 30, 0.85)",
                backdropFilter: "blur(8px)",
                boxShadow:
                  mode === "light"
                    ? "0px 4px 15px rgba(0, 0, 0, 0.1)"
                    : "0px 4px 15px rgba(0, 0, 0, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow:
                    mode === "light"
                      ? "0px 8px 25px rgba(0, 0, 0, 0.15)"
                      : "0px 8px 25px rgba(0, 0, 0, 0.4)",
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor:
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(13, 42, 35, 0.9)",
                backdropFilter: "blur(8px)",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                backgroundColor:
                  mode === "light"
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(30, 30, 30, 0.8)",
                borderRadius: "8px",
              },
            },
          },
        },
      }),
    [mode, i18n.language]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div
          className="App"
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          }}
        >
          <Header />
          <main style={{ flex: 1 }}>
            <Routes>
              {/* المسارات العامة (Public) */}
              <Route path="/" element={<RootRedirect />} />

              <Route path="/login" element={<LoginScreen />} />
              <Route path="/signup" element={<SignUpScreen />} />
              <Route
                path="/forgotpassword"
                element={<ForgotPasswordScreen />}
              />
              <Route path="/verifycode" element={<VerifyCodeScreen />} />
              <Route path="/setpassword" element={<SetPasswordScreen />} />
              <Route path="/WelcomePage" element={<WelcomePage />} />
              {/* بعد تسجيل الدخول، يمكن الوصول إلى هذه المسارات فقط إذا مسجّل */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ContactUs"
                element={
                  <ProtectedRoute>
                    <ContactUs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/adminHome"
                element={
                  <ProtectedRoute>
                    <FamilyManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admins"
                element={
                  <ProtectedRoute>
                    <AdminManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/familyHome"
                element={
                  <ProtectedRoute>
                    <FamilyHome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/FamilyOrders"
                element={
                  <ProtectedRoute>
                    <FamilyOrders />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/FamilyProfile"
                element={
                  <ProtectedRoute>
                    <FamilyProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/AdminProfile"
                element={
                  <ProtectedRoute>
                    <AdminProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product/:id"
                element={
                  <ProtectedRoute>
                    <ServiceDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addProduct"
                element={
                  <ProtectedRoute>
                    <ProductForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/editProduct/:id"
                element={
                  <ProtectedRoute>
                    <ProductForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/Cart"
                element={
                  <ProtectedRoute>
                    <CartPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/MyOrders"
                element={
                  <ProtectedRoute>
                    <MyOrdersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addUser"
                element={
                  <ProtectedRoute>
                    <UserForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/editUser/:id"
                element={
                  <ProtectedRoute>
                    <UserForm />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />

              {/* إذا هناك مسارات أخرى خاصة، غلفها بنفس الطريقة */}
            </Routes>
          </main>
          <Footer />
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={i18n.language === "ar"}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={mode}
        />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
